import React, { useRef, useState } from 'react'
import { Button, Progress, Alert } from 'reactstrap'
import { uploadChatFile } from '../../helpers/chat_helper'

const FileUpload = (props) => {
  const { onFileUploaded, disabled } = props
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState(null)

  const handleFileSelect = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB')
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain']
    if (!allowedTypes.includes(file.type)) {
      setError('File type not allowed. Allowed: Images, PDF, Text')
      return
    }

    try {
      setError(null)
      setUploading(true)
      setUploadProgress(0)

      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90))
      }, 200)

      const response = await uploadChatFile(file)

      clearInterval(progressInterval)
      setUploadProgress(100)

      // Extract attachment data from response
      let attachment = null
      if (response && response.data) {
        attachment = response.data.data || response.data
      }

      if (attachment && onFileUploaded) {
        onFileUploaded(attachment)
      }

      // Reset after success
      setTimeout(() => {
        setUploading(false)
        setUploadProgress(0)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }, 500)

    } catch (err) {
      console.error('File upload error:', err)
      setError(err.response?.data?.message || 'Failed to upload file')
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept="image/*,.pdf,.txt"
        disabled={disabled || uploading}
      />

      {onFileUploaded && typeof onFileUploaded === 'function' && !disabled && !uploading && (
        // This check is just to ensure props are valid, but the render prop is what we care about
        null
      )}

      {/* Use render prop if provided, otherwise default button */}
      {props.render ? (
        props.render(handleButtonClick)
      ) : (
        <Button
          color="light"
          size="sm"
          onClick={handleButtonClick}
          disabled={disabled || uploading}
          title="Attach file"
        >
          <i className="mdi mdi-paperclip"></i>
        </Button>
      )}

      {uploading && (
        <div className="mt-2">
          <Progress value={uploadProgress} className="mb-1" />
          <small className="text-muted">Uploading... {uploadProgress}%</small>
        </div>
      )}

      {error && (
        <Alert color="danger" className="mt-2 py-1 px-2 small" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
    </div>
  )
}

export default FileUpload
